import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { apiClient } from '../../api/apiClient';
import { todayKST } from '../../utils/date';
import EmergencyTeamCalendar from './emergencyteam/EmergencyTeamCalendar';
import { monthKey, monthLabel, rowDraftFromWeek, shiftMonth } from './emergencyteam/helpers';
import {
  boardShellStyle,
  chipStyle,
  chipsStyle,
  messageStyle,
  mobileMonthLabelStyle,
  mobileMonthNavStyle,
  mobileMoveButtonStyle,
  moveButtonStyle,
  pageStyle,
  summaryGridStyle,
  summaryStyle,
} from './emergencyteam/EmergencyTeamManagement.styles';
import {
  stdHeaderRow,
  stdPageTitle,
  stdPageSubtitle,
  stdBlackBtn,
} from './adminlayout/designStandard';

// 회사코드는 위(라우트)에서 받는다 — 전에는 'PINE' 이 박혀 있어 미팅링크에서 자기 배정을
// 못 찾았다 (2026-09-11 C3). 안 넘어오면 'PINE' — 지금까지의 동작.

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function EmergencyTeamManagement({ companyCode = 'PINE' }) {
  const COMPANY_CODE = String(companyCode || 'PINE').toUpperCase();
  const isMobile = useIsMobile();
  const [today] = useState(todayKST());
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [month, setMonth] = useState(Number(today.slice(5, 7)));
  const [data, setData] = useState({ weeks: [], users: [], summary: [], calendar_months: [] });
  const [drafts, setDrafts] = useState({});
  const [monthNotes, setMonthNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [savingNoteKey, setSavingNoteKey] = useState('');
  const [message, setMessage] = useState('');
  const [slideDirection, setSlideDirection] = useState('');
  const [sliding, setSliding] = useState(false);

  const totals = useMemo(() => {
    return data.summary.reduce((acc, row) => {
      acc.weeks += Number(row.week_count || 0);
      acc.people.add(String(row.user_id || row.user_name || ''));
      return acc;
    }, { weeks: 0, people: new Set() });
  }, [data.summary]);

  const currentWeek = useMemo(() => {
    const viewingMonth = `${year}-${String(month).padStart(2, '0')}`;
    if (today.slice(0, 7) !== viewingMonth) return null;
    return data.weeks.find((week) => today <= week.week_end) || null;
  }, [data.weeks, month, today, year]);

  const currentManager = currentWeek?.assignment?.user_name
    || currentWeek?.assignment?.assigned_name
    || (currentWeek ? '미배정' : '-');

  const rangeLabel = useMemo(() => {
    const months = data.calendar_months || [];
    if (!months.length) return monthLabel(year, month);
    const first = months[0];
    const last = months[months.length - 1];
    return `${monthLabel(first.year, first.month)} ~ ${monthLabel(last.year, last.month)}`;
  }, [data.calendar_months, month, year]);

  async function load(nextYear = year, nextMonth = month) {
    setLoading(true);
    setMessage('');
    const params = new URLSearchParams({
      company_code: COMPANY_CODE,
      year: String(nextYear),
      month: String(nextMonth),
    });
    try {
      const res = await apiClient.get(`/api/admin/emergency-team?${params.toString()}`);
      if (!res.ok || !res.data?.success) throw new Error(res.data?.message || '조회에 실패했습니다.');
      const nextData = res.data.data || {};
      const calendarMonths = nextData.calendar_months || [];
      setData({
        weeks: nextData.weeks || [],
        users: nextData.users || [],
        summary: nextData.summary || [],
        calendar_months: calendarMonths,
      });

      const nextDrafts = {};
      const nextMonthNotes = {};
      const allWeeks = calendarMonths.flatMap((item) => item.weeks || []);
      for (const week of allWeeks.length ? allWeeks : (nextData.weeks || [])) {
        nextDrafts[week.week_start] = rowDraftFromWeek(week);
      }
      for (const item of calendarMonths) {
        nextMonthNotes[monthKey(item.year, item.month)] = item.month_note?.memo || '';
      }
      setDrafts(nextDrafts);
      setMonthNotes(nextMonthNotes);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function moveMonth(delta) {
    if (loading || sliding) return;
    const next = shiftMonth(year, month, delta);
    setSlideDirection(delta > 0 ? 'next' : 'prev');
    setSliding(true);
    setYear(next.year);
    setMonth(next.month);
    await load(next.year, next.month);
    window.setTimeout(() => {
      setSliding(false);
      setSlideDirection('');
    }, 260);
  }

  function handleDraft(weekStart, patch) {
    setDrafts((prev) => ({ ...prev, [weekStart]: { ...prev[weekStart], ...patch } }));
  }

  function handleMonthNote(noteYear, noteMonth, value) {
    const key = monthKey(noteYear, noteMonth);
    setMonthNotes((prev) => ({ ...prev, [key]: value }));
  }

  async function saveWeek(weekStart) {
    const draft = drafts[weekStart];
    if (!draft?.user_id) return;
    setSavingKey(weekStart);
    setMessage('');
    const payload = {
      company_code: COMPANY_CODE,
      week_start: weekStart,
      user_id: Number(draft.user_id),
    };
    try {
      const res = await apiClient.post('/api/admin/emergency-team/assignments', payload);
      if (!res.ok || !res.data?.success) throw new Error(res.data?.message || '저장에 실패했습니다.');
      await load(year, month);
      setMessage('저장했습니다.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingKey('');
    }
  }

  async function saveMonthNote(noteYear, noteMonth) {
    const key = monthKey(noteYear, noteMonth);
    setSavingNoteKey(key);
    setMessage('');
    try {
      const res = await apiClient.put('/api/admin/emergency-team/month-note', {
        company_code: COMPANY_CODE,
        year: noteYear,
        month: noteMonth,
        memo: monthNotes[key] || '',
      });
      if (!res.ok || !res.data?.success) throw new Error(res.data?.message || '월 메모 저장에 실패했습니다.');
      await load(year, month);
      setMessage('월 메모를 저장했습니다.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingNoteKey('');
    }
  }

  async function deleteWeek(weekStart) {
    const draft = drafts[weekStart];
    if (!draft?.id) return;
    if (!window.confirm(`${weekStart} 배정을 삭제할까요?`)) return;
    setSavingKey(weekStart);
    setMessage('');
    try {
      const res = await apiClient.del(`/api/admin/emergency-team/assignments/${draft.id}`);
      if (!res.ok || !res.data?.success) throw new Error(res.data?.message || '삭제에 실패했습니다.');
      await load(year, month);
      setMessage('삭제했습니다.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingKey('');
    }
  }

  return (
    <div style={pageStyle}>
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>비상대응팀 관리</h2>
          <div style={stdPageSubtitle}>비상대응팀 명단을 관리합니다.</div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          style={{ ...stdBlackBtn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          disabled={loading}
        >
          <RefreshCw size={14} /> 새로고침
        </button>
      </div>

      <div style={summaryGridStyle(isMobile)}>
        <Summary label="표시 기간" value={isMobile ? monthLabel(year, month) : rangeLabel} compact />
        <Summary label="이번 주 담당" value={currentManager} />
        <Summary label="이번 달 배정" value={`${totals.weeks}주 / ${totals.people.size}명`} />
      </div>

      {data.summary.length > 0 && (
        <div style={chipsStyle}>
          {data.summary.map((row) => (
            <span key={`${row.user_id || row.user_name}`} style={chipStyle}>
              {row.user_name} {row.week_count}주
            </span>
          ))}
        </div>
      )}

      {message && <div style={messageStyle}>{message}</div>}

      {isMobile && (
        <div style={mobileMonthNavStyle}>
          <button type="button" style={mobileMoveButtonStyle('left', loading || sliding)} onClick={() => moveMonth(-1)} disabled={loading || sliding} title="이전 달">
            <ChevronLeft size={24} />
          </button>
          <div style={mobileMonthLabelStyle}>{monthLabel(year, month)}</div>
          <button type="button" style={mobileMoveButtonStyle('right', loading || sliding)} onClick={() => moveMonth(1)} disabled={loading || sliding} title="다음 달">
            <ChevronRight size={24} />
          </button>
        </div>
      )}

      <div style={isMobile ? undefined : boardShellStyle}>
        {!isMobile && (
          <button type="button" style={moveButtonStyle('left')} onClick={() => moveMonth(-1)} disabled={loading || sliding} title="이전 달">
            <ChevronLeft size={34} />
          </button>
        )}
        <EmergencyTeamCalendar
          months={data.calendar_months}
          users={data.users}
          drafts={drafts}
          monthNotes={monthNotes}
          onDraft={handleDraft}
          onMonthNote={handleMonthNote}
          onSave={saveWeek}
          onSaveMonthNote={saveMonthNote}
          onDelete={deleteWeek}
          savingKey={savingKey}
          savingNoteKey={savingNoteKey}
          today={today}
          sliding={sliding}
          slideDirection={slideDirection}
          isMobile={isMobile}
        />
        {!isMobile && (
          <button type="button" style={moveButtonStyle('right')} onClick={() => moveMonth(1)} disabled={loading || sliding} title="다음 달">
            <ChevronRight size={34} />
          </button>
        )}
      </div>
    </div>
  );
}

function Summary({ label, value, compact }) {
  return (
    <div style={summaryStyle}>
      <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: compact ? 16 : 20, fontWeight: 900, color: 'var(--text)' }}>{value}</div>
    </div>
  );
}
